/**
 * Single source of truth for all brand colors.
 * When re-skinning the project, change values ONLY in this file.
 * All other files (tailwind.config.ts, themeColors.ts, pdfTokens.ts, chart components) import from here.
 */
export const colors = {
  // Surface palette (MEP brand)
  peach: {
    DEFAULT: '#F4F8FD',
    1: '#E3ECF8',
    2: '#CCDDF0',
    3: '#B4CAE8',
  },
  pif: {
    green: '#2563B7',
    greenLight: '#97C0F7',
    gold: '#5B90D6',
  },

  // Grey scale — each shade has light-mode and dark-mode value
  grey: {
    black:       { light: '#1E2A39', dark: '#FFFFFF' },
    main:        { light: '#3F5266', dark: '#D8E5F5' },
    hover:       { light: '#8D9399', dark: '#AEBFD4' },
    pseudohover: { light: '#5E7388', dark: '#97C0F7' },
    medium:      { light: '#C2D3E8', dark: '#74879B' },
    plain:       { light: '#E3ECF8', dark: '#314152' },
    light:       { light: '#F4F8FD', dark: '#16212D' },
    sky:         { light: '#FBFDFF', dark: '#0F1720' },
    // Fallback color for sheets without an assigned color
    sheetFallback: '#9EB9DA',
  },

  white: { light: '#FFFFFF', dark: '#16212D' },
  black: { light: '#16212D', dark: '#F4F8FD' },

  // Semantic status colors
  negative: {
    primary:  { light: '#C55363', dark: '#D56A79' },
    hover:    { light: '#D56A79', dark: '#C55363' },
    disable:  '#E1AFB7',
    subtitle: '#F0D1D6',
  },
  positive: {
    primary:  { light: '#2563B7', dark: '#5B90D6' },
    hover:    { light: '#5B90D6', dark: '#2563B7' },
    disable:  '#A9C4E8',
    subtitle: '#D4E2F5',
  },

  brand: {
    main:  '#2563B7',
    gold:  '#5B90D6',
    cream: '#F4F8FD',
    dark:  '#16212D',
  },

  // Chart series palettes (recharts + PDF)
  chartSeries: {
    light: {
      primary:   '#2563B7',
      secondary: '#5B90D6',
      success:   '#7AA6E0',
      warning:   '#D38E4F',
      danger:    '#C55363',
      neutral:   '#8D9399',
      accent_1:  '#97C0F7',
      accent_2:  '#5E7388',
      accent_3:  '#3F5266',
    },
    dark: {
      primary:   '#97C0F7',
      secondary: '#5B90D6',
      success:   '#BCD5F7',
      warning:   '#E1A76A',
      danger:    '#D56A79',
      neutral:   '#AEBFD4',
      accent_1:  '#2563B7',
      accent_2:  '#C55363',
      accent_3:  '#74879B',
    },
  },

  // Avatar / account accent colors (unique vivid palette, not part of main brand)
  avatar: ['#FEAD10', '#3362BD', '#43802A', '#FF67ED', '#00EFFE'],

  // Mermaid diagram theme variables
  mermaid: {
    light: {
      background:         '#ffffff',
      mainBkg:            '#ffffff',
      primaryColor:       '#D7E7FB',
      primaryTextColor:   '#1E2A39',
      primaryBorderColor: '#97C0F7',
      secondaryColor:     '#F4F8FD',
      secondaryTextColor: '#1E2A39',
      tertiaryColor:      '#E3ECF8',
      lineColor:          '#8D9399',
      noteBkgColor:       '#FBFDFF',
      noteTextColor:      '#1E2A39',
      actorTextColor:     '#1E2A39',
      clusterBkg:         '#FBFDFF',
      clusterBorder:      '#C2D3E8',
      edgeLabelBackground:'#E3ECF8',
    },
    dark: {
      background:           '#101A24',
      mainBkg:              '#101A24',
      primaryColor:         '#1C3150',
      primaryTextColor:     '#F4F8FD',
      primaryBorderColor:   '#5B90D6',
      secondaryColor:       '#15212D',
      secondaryTextColor:   '#F4F8FD',
      tertiaryColor:        '#0F1720',
      lineColor:            '#74879B',
      noteBkgColor:         '#0F1720',
      noteTextColor:        '#F4F8FD',
      actorTextColor:       '#F4F8FD',
      clusterBkg:           '#0F1720',
      clusterBorder:        '#5E7388',
      edgeLabelBackground:  '#15212D',
      pieSectionTextColor:  '#F4F8FD',
    },
  },

  // Named shadow tokens (used in tailwind.config.ts boxShadow extension)
  shadows: {
    card:     '0 8px 16px 0 rgba(17, 21, 22, 0.16)',
    subtle:   '0 2px 12px rgba(0, 0, 0, 0.04)',
    compose:  '0 4px 24px rgba(0, 0, 0, 0.10)',
    workflow: '0 4px 20px rgba(37, 99, 183, 0.12)',
  },
} as const;
